import type { FormValues } from '@/core/components/form/formModel'
import { captureWriteIdentity, type WriteIdentity } from '@/core/api/http'
import { insertDoc, renameDoc } from '@/core/data/dokumente'
import { insertSitzung, updateSitzung, type SitzungFields } from '../data/sitzungen'
import { anwesenheitIdByName } from '../data/anwesenheit'
import { validatePresence, findMalformedTimes, ECN_TIME_PATHS, type PresenceRules } from '@/core/lib/validate'
import type { FieldError, FieldErrors } from '@/core/lib/fieldError'
import type { FieldId } from '@/core/lib/result'

export type EcnSitzungFields = {
  datum: string
  beginn: string
  ue: number
  anwesenheit: string
  inhalt: string
  ort: string
  modulId: string
}

async function resolveAnwesenheitId(name: string): Promise<string> {
  const id = await anwesenheitIdByName(name)
  if (!id) {
    throw new Error(
      `Unbekannter Anwesenheit-Wert "${name}" — bitte Seite neu laden und erneut versuchen.`,
    )
  }
  return id
}

function toSitzungFields(fields: EcnSitzungFields, anwesenheitId: string): SitzungFields {
  return {
    datum: fields.datum,
    beginn: fields.beginn,
    ue: fields.ue,
    anwesenheitId,
    inhalt: fields.inhalt,
    ort: fields.ort,
    modulId: fields.modulId,
  }
}

export async function createEcnSitzung(coachingId: string, fields: EcnSitzungFields, identity: WriteIdentity = captureWriteIdentity()): Promise<string> {
  const anwesenheitId = await resolveAnwesenheitId(fields.anwesenheit)
  const dokumentId = await insertDoc({ coachingId, name: 'nachweis-neu' }, identity)
  const sitzungId = await insertSitzung({
    dokumentId,
    ...toSitzungFields(fields, anwesenheitId),
  }, identity)
  await renameDoc(dokumentId, `nachweis-${sitzungId}`, identity)
  return sitzungId
}

export async function updateEcnSitzung(sitzungId: string, fields: EcnSitzungFields, identity: WriteIdentity = captureWriteIdentity()): Promise<void> {
  const anwesenheitId = await resolveAnwesenheitId(fields.anwesenheit)
  await updateSitzung(sitzungId, toSitzungFields(fields, anwesenheitId), identity)
}

export function ecnEntryFromForm(values: FormValues): EcnSitzungFields {
  const s = (values.Sitzung ?? {}) as Record<string, unknown>
  const ueRaw = Number(s['Dauer in UE'])
  return {
    datum: String(s['Datum'] ?? '').trim(),
    beginn: String(s['Beginn'] ?? '').trim(),
    ue: Number.isFinite(ueRaw) && ueRaw > 0 ? ueRaw : 0,
    anwesenheit: String(s['Anwesenheit'] ?? '').trim(),
    inhalt: String(s['Inhalt'] ?? '').trim(),
    ort: String(s['Ort'] ?? '').trim(),
    modulId: String(s['Modul'] ?? '').trim(),
  }
}

export function ecnEntryToFormValues(entry: EcnSitzungFields): FormValues {
  return {
    Sitzung: {
      Datum: entry.datum,
      Beginn: entry.beginn,
      'Dauer in UE': String(entry.ue),
      Anwesenheit: entry.anwesenheit,
      Ort: entry.ort,
      Modul: entry.modulId,
      Inhalt: entry.inhalt,
    },
  }
}

export type EcnCheck = { errors: FieldErrors }

const ecnPresenceRules: PresenceRules = {
  required: [
    'Sitzung.Datum',
    'Sitzung.Beginn',
    'Sitzung.Dauer in UE',
    'Sitzung.Anwesenheit',
    'Sitzung.Ort',
    'Sitzung.Modul',
    'Sitzung.Inhalt',
  ],
}

export const ecnManualFields: ReadonlySet<FieldId> = new Set(ecnPresenceRules.required)

export function checkEcnSitzung(values: Record<string, unknown>): EcnCheck {
  const { missing } = validatePresence(values, ecnPresenceRules)
  const errors = new Map<FieldId, FieldError>()
  for (const path of missing) errors.set(path, { kind: 'missing' })
  for (const path of findMalformedTimes(values, ECN_TIME_PATHS)) {
    if (!errors.has(path)) errors.set(path, { kind: 'malformed', expected: 'time' })
  }
  return { errors }
}
